import queryString from 'query-string';
import { MouseEvent, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { sprintf } from 'sprintf-js';
import styled from 'styled-components';

import { Box } from 'components/Box';
import { Button } from 'components/Button/Button';
import { Hide } from 'components/Hide';
import { Icon } from 'components/Icon';
import { TrackedLink } from 'components/Link';
import { IdealistLogo } from 'components/Logo/IdealistLogo';
import { PageFooter } from 'components/Page/PageFooter';
import { H4 } from 'components/Text/H4';
import { Text } from 'components/Text/Text';
import { showModal } from 'modules/main/store/ducks/modal.actions';
import { useMainDispatch } from 'modules/main/store/hooks/useMainDispatch';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { userDashboardSavedItemsRoute } from 'modules/userDashboard/userDashboard.routes';
import { colors, gutterWidth, transition } from 'theme/theme';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { hasResumeDb } from 'utils/resumeDatabase';

import { MobileNotificationsMenu } from './MobileNotificationsMenu';
import {
  PageHeaderCartPillContainer,
  PageHeaderFooterContainer,
} from './PageHeader.styled';
import { PageHeaderAccountDropdown } from './PageHeaderAccountDropdown';
import { PageHeaderAmpPostListingLink } from './PageHeaderAmpPostListingLink';
import { PageHeaderCartPill } from './PageHeaderCartPill';
import { PAGE_HEADER_NAV } from './PageHeaderConstants';
import { PageHeaderContainer } from './PageHeaderContainer';
import { PageHeaderForOrganizations } from './PageHeaderForOrganizations';
import { PageHeaderMainLink } from './PageHeaderMainLink';
import { PageHeaderMobileLoginButton } from './PageHeaderMobileLoginButton';
import { PageHeaderMobileSectionTitle } from './PageHeaderMobileSectionTitle';
import { PageHeaderNavLink } from './PageHeaderNavLink';
import { PageHeaderNavLinks } from './PageHeaderNavLinks';
import { PageHeaderNotificationsDropdown } from './PageHeaderNotificationsDropdown';
import { PageHeaderPostListingLink } from './PageHeaderPostListingLink';
import { PageHeaderSeparator } from './PageHeaderSeparator';

const LoginLink = styled.a`
  color: ${colors.mediumContentGrey};
  font-size: inherit;
  transition: color ${transition};
  &,
  &:hover,
  &:focus {
    text-decoration: none;
  }
  &:hover,
  &:focus {
    color: ${colors.brandBlue};
  }
`;

type Props = {
  urlByLocale: Record<UserLocale, string | undefined> | undefined;
  minimalNav?: boolean;
  navSection?: 'MAIN' | 'STAFF';
  toggleSearch: (val?: boolean) => void;
  withSearchHero?: boolean;
  requestIsAmp?: boolean;
};

export function PageHeader({
  urlByLocale,
  minimalNav,
  navSection = 'MAIN',
  toggleSearch,
  withSearchHero,
  requestIsAmp,
}: Props) {
  const userEnvironment = useUserEnvironment();
  const { user, isUserLoaded } = userEnvironment;

  const dispatch = useMainDispatch();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  // const [notificationsMenuOpen, setNotificationsMenuOpen] = useState(false);
  const redirectTo = location ? `${location.pathname}${location.search}` : '';
  const redirectParam = queryString.stringify({
    to: redirectTo,
  });

  const fullSize = (withSearchHero || requestIsAmp) && !minimalNav;
  const isStaff = navSection === 'STAFF';
  const navOptions: typeof PAGE_HEADER_NAV.STAFF | typeof PAGE_HEADER_NAV.MAIN =
    navSection === 'STAFF' ? PAGE_HEADER_NAV.STAFF : PAGE_HEADER_NAV.MAIN;

  const nonAmpUrl = location.pathname.replace('/amp/', '/').replace(/#.+$/, '');
  const clickLogin = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      trackClicked('Log In Header');
      dispatch(
        showModal('LOGIN', {
          redirectTo,
          manualLogin: true,
        }),
      );
    },
    [redirectTo, dispatch],
  );

  useEffect(() => {
    if (location.hash === '#search') {
      toggleSearch(true);
    } else if (location.hash === '#menu') {
      setMenuOpen(true);
    }
  }, [location.hash, toggleSearch]);

  return (
    <PageHeaderContainer
      $fullSize={fullSize}
      position="relative"
      zIndex="101"
      height={[60, null, null, fullSize ? 72 : 40]}
    >
      <Box
        classes={['flex', 'align-center', 'justify-between']}
        position={['fixed', null, null, 'relative']}
        top={0}
        left={0}
        width={1}
        height={[60, null, null, fullSize ? 72 : 40]}
        px={gutterWidth / 2}
        bg="white"
        boxShadow={['0 2px 4px rgba(0,0,0,0.075)', null, null, 'none']}
        pointerEvents="auto"
      >
        <Box
          mr={24}
          flex="0 0 auto"
          // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'Display | u... Remove this comment to see the full error message
          display={[null, null, null, !fullSize && 'none']}
        >
          <TrackedLink
            href={isStaff ? '/staff' : '/'}
            title={`Idealist${isStaff ? ' Staff' : ''}`}
            eventName="Idealist Logo Header"
            data-qa-id="header-logo-link"
          >
            <div style={{ lineHeight: 0 }}>
              <IdealistLogo
                withText
                withStaffMarker={isStaff}
                height={{ mobile: 28, desktop: 36 }}
              />
            </div>
          </TrackedLink>
        </Box>

        <Hide lg xl>
          <Box ml="auto" classes={['flex', 'align-center']}>
            {requestIsAmp ? (
              <>
                <a
                  href={`${nonAmpUrl}#search`}
                  aria-label={getText('Toggle search')}
                  title={getText('Toggle search')}
                  data-qa-id="search-menu-button"
                >
                  <Icon name="search" size={22} colorName="darkContentGrey" />
                </a>

                <Box
                  component="a"
                  href={`${nonAmpUrl}#menu`}
                  aria-label={getText('Toggle menu')}
                  title={getText('Toggle menu')}
                  ml={16}
                  data-qa-id="mobile-menu-button"
                >
                  <Icon
                    name="hamburger"
                    size={22}
                    colorName="darkContentGrey"
                  />
                </Box>
              </>
            ) : (
              <>
                <div>
                  <Button
                    onClick={() => toggleSearch()}
                    aria-label={getText('Toggle search')}
                    title={getText('Toggle search')}
                    data-qa-id="search-menu-button"
                    variant="tertiary-grey"
                    icon={{ type: 'normalized', name: 'search' }}
                  />
                </div>

                {user && <MobileNotificationsMenu />}

                <div style={{ marginLeft: 4 }}>
                  <Button
                    onClick={() => setMenuOpen(!menuOpen)}
                    aria-label={getText('Toggle menu')}
                    title={getText('Toggle menu')}
                    data-qa-id="mobile-menu-button"
                    variant="tertiary-grey"
                    icon={{ type: 'normalized', name: 'hamburger' }}
                  />
                </div>
              </>
            )}
          </Box>
        </Hide>

        <PageHeaderNavLinks
          flex="1 1 auto"
          $menuOpen={menuOpen}
          onClick={() => setMenuOpen(false)}
          height={[null, null, null, '100%']}
        >
          <Hide lg xl height={[null, null, null, '100%']}>
            <Box
              classes={['flex', 'align-center', 'justify-between']}
              px={gutterWidth / 2}
              height={60}
              width="100%"
              borderBottom={`1px solid ${colors.selectionGrey}`}
            >
              <H4 mb={0} color={colors.brandBlue}>
                {user
                  ? /* TRANSLATORS: user greeting at top of page */
                    sprintf(getText('Hi, %(name)s!'), {
                      name: user.firstName || user.email,
                    })
                  : getText('Menu')}
              </H4>

              <div style={{ margin: '0 auto' }}>
                <Button
                  onClick={() => setMenuOpen(false)}
                  aria-label={getText('Close search')}
                  variant="tertiary-grey"
                  icon={{ type: 'normalized', name: 'close' }}
                />
              </div>
            </Box>
          </Hide>

          <Box
            display={['block', null, null, 'flex']}
            height={[null, null, null, '100%']}
            justifyContent="space-between"
            p={[gutterWidth / 2, null, null, 0]}
            pb={[0, null, null, 0]}
            data-qa-id="nav-dropdown-mobile"
          >
            <Box
              display={['block', null, null, 'flex']}
              alignItems="center"
              mt="1px"
            >
              {navOptions.map((navOption, index) => (
                <PageHeaderMainLink
                  key={navOption}
                  index={index}
                  navOption={navOption}
                  navSection={navSection}
                  fullSize={Boolean(fullSize)}
                />
              ))}
            </Box>

            <Box display={['block', null, null, 'flex']} alignItems="center">
              {user ? (
                <>
                  <Hide lg xl>
                    <PageHeaderMobileSectionTitle>
                      {getText('My Account')}
                    </PageHeaderMobileSectionTitle>

                    <PageHeaderNavLink
                      isStaff={isStaff}
                      type="USER_DASHBOARD"
                      to={userDashboardSavedItemsRoute.with({})}
                    >
                      {getText('My Saved Items')}
                    </PageHeaderNavLink>

                    {hasResumeDb(userEnvironment) && (
                      <PageHeaderNavLink
                        isStaff={isStaff}
                        type="USER_DASHBOARD"
                        to="/dashboard/employment-profile"
                      >
                        {getText('Profile')}
                      </PageHeaderNavLink>
                    )}

                    <PageHeaderNavLink
                      data-qa-id="page-header-account-dropdown-account-settings"
                      isStaff={isStaff}
                      type="USER_DASHBOARD"
                      to="/dashboard/account-settings"
                    >
                      {getText('Settings')}
                    </PageHeaderNavLink>

                    {user.orgs.length > 0 && (
                      <>
                        <PageHeaderMobileSectionTitle>
                          {getText('Manage')}
                        </PageHeaderMobileSectionTitle>

                        {user.orgs.map((org) => (
                          <Box key={org.id}>
                            <PageHeaderNavLink
                              isStaff={isStaff}
                              type="ORG_DASHBOARD"
                              to={
                                org.hasGradProgram
                                  ? `/admin/${org.id}/grad-program`
                                  : `/admin/${org.id}`
                              }
                            >
                              {org.name}
                            </PageHeaderNavLink>
                          </Box>
                        ))}
                      </>
                    )}
                  </Hide>

                  <Box
                    display={['none', 'none', 'none', 'flex']}
                    alignItems="center"
                  >
                    <PageHeaderAccountDropdown isStaff={isStaff} />
                    <PageHeaderSeparator />
                  </Box>

                  <Box
                    display={['none', 'none', 'none', 'flex']}
                    alignItems="center"
                  >
                    <PageHeaderNotificationsDropdown />
                  </Box>
                </>
              ) : (
                <Box display={['none', null, null, 'flex']} alignItems="center">
                  <LoginLink
                    href={`/login?${redirectParam}`}
                    onClick={clickLogin}
                    data-qa-id="login-link"
                    aria-label={getText('Log In')}
                  >
                    {(isUserLoaded || requestIsAmp) && getText('Log In')}
                  </LoginLink>
                  <PageHeaderSeparator style={{ marginLeft: 12 }} />
                </Box>
              )}

              {requestIsAmp ? (
                <Hide xs sm md>
                  <Box
                    component={LoginLink}
                    mx={12}
                    classes={['flex', 'align-center']}
                    href={`${nonAmpUrl}#search`}
                    aria-label={getText('Toggle search')}
                    title={getText('Toggle search')}
                    data-qa-id="search-menu-button"
                  >
                    <Box mr="6px">
                      <Icon name="search" size={18} />
                    </Box>
                    {getText('Search')}
                  </Box>
                </Hide>
              ) : null}

              {!isStaff && (
                <>
                  <Hide xs sm md>
                    <PageHeaderSeparator />
                  </Hide>
                  <PageHeaderForOrganizations />
                  <Hide xs sm md>
                    <PageHeaderSeparator style={{ marginRight: 12 }} />
                  </Hide>
                  <PageHeaderMobileSectionTitle />

                  {requestIsAmp ? (
                    <PageHeaderAmpPostListingLink />
                  ) : (
                    <PageHeaderPostListingLink />
                  )}

                  {user && Boolean(user.orgs?.length) && (
                    <PageHeaderCartPillContainer>
                      <PageHeaderSeparator
                        style={{ marginLeft: 12, marginRight: 12 }}
                      />

                      <PageHeaderCartPill />
                    </PageHeaderCartPillContainer>
                  )}

                  <Hide lg xl>
                    <PageHeaderNavLink
                      to="/organizations/create"
                      data-qa-id="create-org"
                      onClick={() => {
                        trackClicked('Add Your Organization');
                      }}
                      type="ORG"
                    >
                      <Box classes={['flex', 'align-center']}>
                        <Box mr={[14, null, null, '6px']} mt="-4px">
                          <Icon name="circle-add" size={18} />
                        </Box>
                        <Text fontSize="inherit" fontWeight="normal">
                          {getText('Add Your Organization')}
                        </Text>
                      </Box>
                    </PageHeaderNavLink>

                    <PageHeaderMobileSectionTitle />

                    <PageHeaderMobileLoginButton
                      isLoggedIn={Boolean(user)}
                      redirectParam={redirectParam}
                      onClickLogin={clickLogin}
                    />

                    <PageHeaderFooterContainer>
                      {menuOpen && (
                        <PageFooter urlByLocale={urlByLocale} inPageHeader />
                      )}
                    </PageHeaderFooterContainer>
                  </Hide>
                </>
              )}
            </Box>
          </Box>
        </PageHeaderNavLinks>
      </Box>
    </PageHeaderContainer>
  );
}
